
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { ResellerPlan } from '@/types/licenseKey';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DialogFooter } from '@/components/ui/dialog';

const licenseFormSchema = z.object({
  domain: z.string().optional(),
  plan: z.literal('enterprise'), // Only enterprise plan is allowed
  expiresAt: z.date({
    required_error: "An expiration date is required.",
  }),
  notes: z.string().optional(),
});

export type LicenseFormValues = z.infer<typeof licenseFormSchema>;

interface CreateLicenseFormProps {
  onSubmit: (data: LicenseFormValues) => Promise<void>;
  isCreatingLicense: boolean;
}

const CreateLicenseForm: React.FC<CreateLicenseFormProps> = ({ 
  onSubmit, 
  isCreatingLicense 
}) => {
  const form = useForm<LicenseFormValues>({
    resolver: zodResolver(licenseFormSchema),
    defaultValues: {
      domain: '',
      plan: 'enterprise', // Set default to enterprise
      expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), // Default to 1 year
      notes: '',
    },
  });
  
  const handleSubmit = async (data: LicenseFormValues) => {
    try {
      await onSubmit(data);
      form.reset();
    } catch (error) {
      console.error('Failed to create license:', error);
    }
  };
  
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="plan"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Reseller Plan</FormLabel>
              <FormControl>
                <Input 
                  value="Reseller Pro (10 instances)" 
                  readOnly 
                  disabled 
                  className="bg-gray-100"
                />
              </FormControl>
              <FormDescription>
                All licenses are created with the Reseller Pro plan
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="domain"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Domain (Optional)</FormLabel>
              <FormControl>
                <Input placeholder="example.referme.site" {...field} />
              </FormControl>
              <FormDescription>
                Pre-assign a domain to this license
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="expiresAt"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>Expiration Date</FormLabel>
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant={"outline"}
                      className="w-full pl-3 text-left font-normal"
                    >
                      {field.value ? (
                        format(field.value, "PPP")
                      ) : (
                        <span>Pick a date</span>
                      )}
                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={field.value}
                    onSelect={field.onChange}
                    disabled={(date) =>
                      date < new Date()
                    }
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <FormDescription>
                When this license will expire
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes (Optional)</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Additional notes about this license"
                  className="resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <DialogFooter>
          <Button type="submit" disabled={isCreatingLicense}>
            {isCreatingLicense ? "Creating..." : "Create License Key"}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};

export default CreateLicenseForm;
